// Modelli per la libreria personalizzata di attività (personal/agency activity library).
// Le RPC lato Supabase (get_activity_library, save_activity_to_library,
// add_library_activity_to_itinerary) fanno tutto il lavoro di persistenza/RLS:
// qui viviamo solo di mapping tra la riga DB (ActivityLibraryRow) e le
// classi/interfacce già usate dal builder (ItineraryActivity / ActivityFactory).

import { Json } from '@/integrations/supabase/types';
import { ActivityLibraryRow } from '@/integrations/supabase/rpc-types';
import {
  ActivityType,
  ActivityDocumentation,
  ItineraryActivity,
  ActivityFactory,
} from '@/lib/builder/models/activity.model';

export type LibraryOwnerType = 'user' | 'agency';

export interface ILibraryActivity {
  id: string;
  owner_type: LibraryOwnerType;
  owner_id: string;
  activity_type: ActivityType;
  title: string;
  description: string | null;
  location: string | null;
  address: string | null;
  start_time: string | null;
  end_time: string | null;
  start_date: string | null;
  end_date: string | null;
  cost: number | null;
  include_price: boolean;
  price_tag: string | null;
  link: string | null;
  company: string | null;
  transport_type: 'train' | 'bus' | 'car' | 'taxi' | 'metro' | 'other' | null;
  travels: Json;
  check_in_time: string | null;
  check_out_time: string | null;
  check_in_note: string | null;
  refundable_rate: boolean;
  media: Json;
  documentation: Json;
  tags: string[];
  metadata: Json;
  created_at: string;
  updated_at: string;
}

/**
 * Payload accettato dalla RPC `save_activity_to_library` (p_activity).
 * `id` va valorizzato SOLO per aggiornare una entry esistente e modificabile:
 * se assente (o non modificabile) la RPC esegue un insert.
 */
export type LibraryActivityPayload = Partial<Omit<ILibraryActivity, 'created_at' | 'updated_at'>>;

export class LibraryActivity implements ILibraryActivity {
  id: string;
  owner_type: LibraryOwnerType;
  owner_id: string;
  activity_type: ActivityType;
  title: string;
  description: string | null;
  location: string | null;
  address: string | null;
  start_time: string | null;
  end_time: string | null;
  start_date: string | null;
  end_date: string | null;
  cost: number | null;
  include_price: boolean;
  price_tag: string | null;
  link: string | null;
  company: string | null;
  transport_type: 'train' | 'bus' | 'car' | 'taxi' | 'metro' | 'other' | null;
  travels: Json;
  check_in_time: string | null;
  check_out_time: string | null;
  check_in_note: string | null;
  refundable_rate: boolean;
  media: Json;
  documentation: Json;
  tags: string[];
  metadata: Json;
  created_at: string;
  updated_at: string;

  constructor(data: ILibraryActivity) {
    Object.assign(this, data);
  }

  /**
   * Converte questa entry di libreria in un'istanza di attività del builder,
   * pronta per essere aggiunta a un itinerario in una data specifica.
   * @param date data target nell'itinerario (default: start_date della entry)
   */
  toItineraryActivity(date?: string): ItineraryActivity {
    return ActivityFactory.create({
      id: crypto.randomUUID(),
      title: this.title,
      description: this.description ?? '',
      location: this.location ?? '',
      type: this.activity_type,
      date: date ?? this.start_date ?? '',
      include_price: this.include_price,
      address: this.address ?? undefined,
      start_date: this.start_date ?? undefined,
      end_date: this.end_date ?? undefined,
      start_time: this.start_time ?? undefined,
      end_time: this.end_time ?? undefined,
      check_in_time: this.check_in_time ?? undefined,
      check_out_time: this.check_out_time ?? undefined,
      check_in_note: this.check_in_note ?? undefined,
      refundable_rate: this.refundable_rate,
      price_tag: this.price_tag ?? undefined,
      link: this.link ?? undefined,
      cost: this.cost ?? undefined,
      company: this.company ?? undefined,
      transport_type: this.transport_type ?? undefined,
      media: (this.media as unknown as string[]) ?? [],
      documentation: (this.documentation as unknown as ActivityDocumentation[]) ?? [],
      travels: (this.travels as unknown as ItineraryActivity['travels']) ?? [],
      metadata: (this.metadata as unknown as Record<string, unknown>) ?? {},
    } as ItineraryActivity);
  }
}

/**
 * Factory per creare/convertire LibraryActivity, seguendo il pattern già usato
 * da ActivityFactory in src/lib/builder/models/activity.model.tsx.
 */
export class LibraryActivityFactory {
  static fromRow(row: ActivityLibraryRow): LibraryActivity {
    return new LibraryActivity(row as ILibraryActivity);
  }

  /**
   * Costruisce il payload per `save_activity_to_library` a partire da un'attività
   * del builder. Passare `id` solo per aggiornare una entry di libreria esistente.
   */
  static fromItineraryActivity(
    activity: ItineraryActivity,
    options?: { id?: string; tags?: string[] }
  ): LibraryActivityPayload {
    const payload: LibraryActivityPayload = {
      activity_type: activity.type,
      title: activity.title,
      description: activity.description || null,
      location: activity.location || null,
      address: activity.address ?? null,
      start_time: activity.start_time ?? null,
      end_time: activity.end_time ?? null,
      start_date: activity.start_date ?? null,
      end_date: activity.end_date ?? null,
      cost: activity.cost ?? null,
      include_price: activity.include_price ?? false,
      price_tag: activity.price_tag ?? null,
      link: activity.link ?? null,
      company: activity.company ?? null,
      transport_type: activity.transport_type ?? null,
      travels: (activity.travels as unknown as Json) ?? [],
      check_in_time: activity.check_in_time ?? null,
      check_out_time: activity.check_out_time ?? null,
      check_in_note: activity.check_in_note ?? null,
      refundable_rate: activity.refundable_rate ?? false,
      media: (activity.media as unknown as Json) ?? [],
      documentation: (activity.documentation as unknown as Json) ?? [],
      tags: options?.tags ?? [],
      metadata: (activity.metadata as unknown as Json) ?? {},
    };
    if (options?.id) {
      payload.id = options.id;
    }
    return payload;
  }
}
